import React, { useEffect, useState } from 'react'
import { Box, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ModalInput from '../../forms/modalForms/modalInput'
import ModalSelect from '../../forms/modalForms/modalSelect'
import ModalTextarea from '../../forms/modalForms/modalTextarea'
import FormModal from '../../common/FormModal'
import { useFormMutation } from '../../../hooks/useFormMutation'
import {
  createVacancy,
  updateVacancy,
  fetchJobProfiles,
} from '../../../api/recruitment/recruitment'
import { countrySelectOptions } from '../../countries/PhoneDial'
import { Vacancy } from '../../../types/recruitment/recruitmentTypes'

interface VacancyTextModalProps {
  isOpen: boolean
  onClose: () => void
  vacancy?: Vacancy
}

const VacancyTextModal: React.FC<VacancyTextModalProps> = ({
  isOpen,
  onClose,
  vacancy,
}) => {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    title: '',
    job_profile: '',
    country: '',
    description: '',
  })
  const [errors, setErrors] = useState<{ title?: string; country?: string; job_profile?: string }>({})

  const jobProfiles = useQuery({
    queryKey: ['job-profiles'],
    queryFn: fetchJobProfiles,
  })

  useEffect(() => {
    setErrors({})
    if (vacancy) {
      setForm({
        title: vacancy.title || '',
        job_profile: typeof vacancy.job_profile === 'object' && vacancy.job_profile !== null
          ? String(vacancy.job_profile.id)
          : String(vacancy.job_profile || ''),
        country: vacancy.country || '',
        description: vacancy.description || '',
      })
    } else {
      setForm({ title: '', job_profile: '', country: '', description: '' })
    }
  }, [vacancy, isOpen])

  const handleClose = () => {
    setForm({ title: '', job_profile: '', country: '', description: '' })
    setErrors({})
    onClose()
  }

  const saveMutation = useFormMutation({
    mutationFn: (payload) => vacancy ? updateVacancy(payload as Parameters<typeof updateVacancy>[0]) : createVacancy(payload as Parameters<typeof createVacancy>[0]),
    queryKey: 'vacancy-texts',
    onClose: handleClose,
  })

  const handleSubmit = () => {
    const newErrors: { title?: string; country?: string; job_profile?: string } = {}
    if (!form.title.trim()) newErrors.title = t('validation.required', { field: t('common.fields.title') })
    if (!form.job_profile) newErrors.job_profile = t('validation.required', { field: t('recruitment.fields.jobProfile') })
    if (!form.country) newErrors.country = t('validation.required', { field: t('common.fields.country') })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    setErrors({})

    const payload = {
      title: form.title,
      job_profile: form.job_profile,
      country: form.country,
      description: form.description,
    }

    saveMutation.mutate(vacancy ? { id: vacancy.id, ...payload } : payload)
  }

  const rawJobProfiles: { id: string | number; title: string }[] = Array.isArray(jobProfiles.data?.data)
    ? jobProfiles.data.data
    : Array.isArray(jobProfiles.data?.results)
      ? jobProfiles.data.results
      : Array.isArray(jobProfiles.data)
        ? jobProfiles.data
        : []

  const jobProfileOptions = rawJobProfiles.map((p) => ({
    value: String(p.id),
    label: p.title,
  }))

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={vacancy ? t('recruitment.modals.vacancy.editTitle') : t('recruitment.modals.vacancy.addTitle')}
      onSubmit={handleSubmit}
      isLoading={saveMutation.isPending}
      submitLabel={vacancy ? t('common.actions.saveChanges') : t('recruitment.modals.vacancy.createButton')}
      size="lg"
    >
      <VStack spacing={4} width="100%">
        <Box w="100%">
          <ModalInput
            label={t('common.fields.title')}
            name="title"
            value={form.title}
            onChange={(e) => { setForm((prev) => ({ ...prev, title: e.target.value })); if (errors.title) setErrors((prev) => ({ ...prev, title: undefined })) }}
            placeholder={t('recruitment.placeholders.enterVacancyTitle')}
            required
            isInvalid={!!errors.title}
          />
          {errors.title && <Text textStyle="b-xs" color="error.100" mt={1}>{errors.title}</Text>}
        </Box>

        <Box w="100%">
          <ModalSelect
            label={t('recruitment.fields.jobProfile')}
            name="job_profile"
            value={form.job_profile}
            options={jobProfileOptions}
            onChange={(e) => { setForm((prev) => ({ ...prev, job_profile: e.target.value })); if (errors.job_profile) setErrors((prev) => ({ ...prev, job_profile: undefined })) }}
            placeholder={
              jobProfiles.isLoading
                ? t('common.states.loading')
                : jobProfileOptions.length === 0
                  ? t('recruitment.placeholders.noJobProfiles')
                  : t('recruitment.placeholders.selectJobProfile')
            }
            searchDisabled={false}
            required
            isInvalid={!!errors.job_profile}
          />
          {errors.job_profile && <Text textStyle="b-xs" color="error.100" mt={1}>{errors.job_profile}</Text>}
        </Box>

        <Box w="100%">
          <ModalSelect
            label={t('common.fields.country')}
            name="country"
            value={form.country}
            options={countrySelectOptions}
            onChange={(e) => { setForm((prev) => ({ ...prev, country: e.target.value })); if (errors.country) setErrors((prev) => ({ ...prev, country: undefined })) }}
            showCountryFlag
            required
            isInvalid={!!errors.country}
          />
          {errors.country && <Text textStyle="b-xs" color="error.100" mt={1}>{errors.country}</Text>}
        </Box>

        <ModalTextarea
          label={t('common.fields.description')}
          name="description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder={t('recruitment.placeholders.vacancyText')}
          minH="160px"
        />
      </VStack>
    </FormModal>
  )
}

export default VacancyTextModal
